import { useQuery } from "@tanstack/react-query";
import { Linkedin } from "lucide-react";
import { AvatarPreset, EmptyState, SmartImage } from "@ec2/ui";
import { useT } from "@ec2/i18n/react";
import { useTRPC } from "../lib/api";
import { appConfig } from "../lib/config";
import { safeLinkedinHref } from "../lib/links";
import { useActive } from "../state/hooks";
import { Shell } from "../components/Shell";
import { LoadingLine, ScreenHeader } from "../components/common";

/** Contacts (spec §2): avatar, name, LinkedIn glyph, shared interests highlighted. */
export function ContactsScreen() {
  const t = useT();
  const trpc = useTRPC();
  const { active } = useActive();
  const contacts = useQuery({
    ...trpc.contact.list.queryOptions({ eventId: active?.eventId ?? "" }),
    enabled: Boolean(active),
  });
  const rows = contacts.data ?? [];

  return (
    <Shell active="profile">
      <ScreenHeader title={t("ec2.contacts.title")} />
      <div className="flex flex-col gap-3 px-4" data-testid="ec-contacts">
        {contacts.isLoading && <LoadingLine />}
        {contacts.data && rows.length === 0 && (
          <EmptyState title={t("ec2.contacts.empty.title")} body={t("ec2.contacts.empty.body")} accentClassName="text-primary" />
        )}
        {rows.map((contact) => {
          // Stored-XSS guard: only http(s) linkedin.com URLs become links.
          const linkedinHref = safeLinkedinHref(contact.linkedinUrl);
          return (
          <div
            key={contact.contactId}
            data-testid="ec-contact-row"
            className="flex items-start gap-3 rounded-(--radius-card) border border-border bg-surface px-4 py-3"
          >
            {contact.avatarKey ? (
              <SmartImage
                base={appConfig.mediaBase}
                mediaKey={contact.avatarKey}
                alt={t("ec2.a11y.avatar", { name: contact.displayName })}
                width={40}
                height={40}
                maxVariant="thumb"
                className="size-10 rounded-full object-cover"
              />
            ) : (
              <AvatarPreset preset={contact.avatarPresetId} size={40} label={t("ec2.a11y.avatar", { name: contact.displayName })} />
            )}
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-2 text-base font-semibold text-fg">
                <span className="truncate">{contact.displayName}</span>
                {linkedinHref && (
                  <a
                    href={linkedinHref}
                    target="_blank"
                    rel="noreferrer noopener"
                    aria-label={t("ec2.profile.linkedin")}
                    className="text-accent focus-visible:outline-2 focus-visible:outline-primary"
                  >
                    <Linkedin size={16} aria-hidden />
                  </a>
                )}
              </p>
              {(contact.interests.length > 0 || contact.sharedInterests.length > 0) && (
                <p className="mt-1 flex flex-wrap gap-1.5">
                  {contact.interests.map((interest) => {
                    const shared = contact.sharedInterests.includes(interest);
                    return (
                      <span
                        key={interest}
                        data-shared={shared || undefined}
                        aria-label={shared ? t("ec2.contacts.sharedInterest", { interest }) : undefined}
                        className={
                          `rounded-(--radius-chip) px-2 py-0.5 text-[13px] font-medium ` +
                          (shared ? "bg-accent/20 text-accent" : "bg-surface-overlay text-fg-secondary")
                        }
                      >
                        {interest}
                      </span>
                    );
                  })}
                </p>
              )}
            </div>
          </div>
          );
        })}
      </div>
    </Shell>
  );
}
